import { PhoenixUser } from 'phoenix/domain';
import {UserService} from 'sharebase/services/userService';

const NewUser:Partial<PhoenixUser> = {
	UserName: null,
	EmailAddress: null,
	Password: null,
	Embedded: {
		Roles: [],
		AdministratorOfDeploymentGroups: [],
		Deployment: {
			DeploymentId: null,
			Name: null,
			Embedded: {
				DeploymentGroup: null
			}
		},
		UserGroups: null
	}
}

export default class UserDataStore {
	public static $inject = [
		'$q',
		'sbUserService'
	];

	constructor(
		private $q:ng.IQService,
		private sbUserService:UserService
	) {
	}

	private _user:PhoenixUser = null;
	private _startState = null;
	
	/**
	* @ngdoc property
	* @name sbUserDataStore#user
	* @propertyOf sharebase.sbUserDataStore
	* @kind property
	*
	* @description
	* A user
	* 
	* @returns {object} A user
	*/
	public get user() { return this._user; }
	/**
	 * @ngdoc property
	 * @name sbUserDataStore#userGroups
	 * @propertyOf sharebase.sbUserDataStore
	 * @kind property
	 * 
	 * @description
	 * A list of user groups the user is part of
	 * 
	 * @returns {array} An array of user group names and ids.
	 */
	public get userGroups() { return this._user ? this._user.Embedded.UserGroups : null; }
	/**
	* @ngdoc property
	* @name sbUserDataStore#isTenantAdmin
	* @propertyOf sharebase.sbUserDataStore
	* @kind property
	*
	* @description
	* Set user tenant admin role
	*/
	public set isTenantAdmin(val) { this.setTenantAdmin(val); }
	/**
	* @ngdoc property
	* @name sbUserDataStore#isTenantAdmin
	* @propertyOf sharebase.sbUserDataStore
	* @kind property
	*
	* @description
	* Get user tenant admin role
	*/
	public get isTenantAdmin() { return this._user ? this._user.Embedded.Roles.indexOf('TenantAdmin') > -1 : false; }
	/**
	* @ngdoc property
	* @name sbUserDataStore#deploymentGroupsAdmin
	* @propertyOf sharebase.sbUserDataStore
	* @kind property
	*
	* @description
	* Set the deployment groups for this user administers.
	* 
	*/
	public set deploymentGroupAdmin(val) { this.setDeploymentGroupAdmin(val); }
	/**
	* @ngdoc property
	* @name sbUserDataStore#deploymentGroupsAdmin
	* @propertyOf sharebase.sbUserDataStore
	* @kind property
	*
	* @description
	* Get the deployment groups for this user administers.
	* 
	* @returns {array} A deployment group.
	*/
	public get deploymentGroupAdmin() { return this._user ? this._user.Embedded.AdministratorOfDeploymentGroups[0] : null; }
	/**
	* @ngdoc property
	* @name sbUserDataStore#isSiteAdmin
	* @propertyOf sharebase.sbUserDataStore
	* @kind property
	*
	* @description
	* Set user site admin role
	*/
	public set isSiteAdmin(val) { this.setSiteAdmin(val);}
	
	/**
	* @ngdoc property
	* @name sbUserDataStore#isSiteAdmin
	* @propertyOf sharebase.sbUserDataStore
	* @kind property
	*
	* @description
	* Get user site admin role
	*/
	public get isSiteAdmin() { return this._user ? this._user.Embedded.Roles.indexOf('SiteAdmin') > -1 : false;}
	/**
	* @ngdoc property
	* @name sbUserDataStore#isUsereAdmin
	* @propertyOf sharebase.sbUserDataStore
	* @kind property
	*
	* @description
	* Set user user admin role
	*/
	public set isUserAdmin(val) { this.setUserAdmin(val);}
	/**
	* @ngdoc property
	* @name sbUserDataStore#isUsereAdmin
	* @propertyOf sharebase.sbUserDataStore
	* @kind property
	*
	* @description
	* Get user user admin role
	*/
	public get isUserAdmin() { return this._user ? this._user.Embedded.Roles.indexOf('UserAdmin') > -1 : false;}

	/**
	* @ngdoc method
	* @name sbUserDataStore#load
	* @methodOf sharebase.sbUserDataStore
	* @kind function
	*
	* @description
	* Gets a user
	*
	* @returns {Deferred} A promise that will resolve with a user
	* 
	*/
	public load(userId?:number) {
		let p:ng.IPromise<PhoenixUser|Partial<PhoenixUser>> = this.$q.resolve(angular.copy(NewUser));

		if (userId) {
			p = this.sbUserService
					.getUser(userId)
					.then(response => {
						//clean duplicate roles
						response.Embedded.Roles = response.Embedded.Roles.filter((v, i, a) => a.indexOf(v) === i);
						return response;
					});
		}

		return p.then((user:PhoenixUser) => {
			this._user = user;
			this._startState = angular.copy(this._user);
			return user;
		});
	}

	/**
	* @ngdoc method
	* @name sbUserDataStore#addUser
	* @methodOf sharebase.sbUserDataStore
	* @kind function
	*
	* @description
	* Adds a new user
	*
	* @returns {Deferred} A promise that will resolve with a new user
	* 
	*/
	public addUser(user:PhoenixUser, deploymentId:number) {
		return this.sbUserService.addUser(user, deploymentId);
	}
	
	/**
	* @ngdoc method
	* @name sbUserDataStore#removeUser
	* @methodOf sharebase.sbUserDataStore
	* @kind function
	*
	* @description
	* Deletes a user and transfers content to another user
	*
	* @returns {Deferred} A promise that will resolve with status result
	* 
	*/
	public removeUser(user:PhoenixUser, deploymentId:number, transferUser?:PhoenixUser, forceDelete?:boolean) {
		return this.sbUserService.removeUser(user, deploymentId, transferUser, forceDelete)
	}

	/**
	* @ngdoc method
	* @name sbUserDataStore#addEditGroupAdmin
	* @methodOf sharebase.sbUserDataStore
	* @kind function
	*
	* @description
	* Adds a new Administrator (Tenant/DeploymentGroup)
	*
	* @returns {Deferred} A promise that will resolve with a new Administrator (Tenant/DeploymentGroup)
	* 
	*/
	public updateTenantAdminRights() {
		let depGroup = this._user.Embedded.AdministratorOfDeploymentGroups[0];
		let startDepGroup = this._startState.Embedded.AdministratorOfDeploymentGroups[0];
		let rightChanged = !angular.equals(depGroup, startDepGroup);  
		let p:ng.IPromise<any> = this.$q.resolve();

		if(rightChanged) {
			if (this.isTenantAdmin) {
				if(startDepGroup) {
					p = this.sbUserService.removeGroupAdmin(startDepGroup.DeploymentGroupId, this._user.UserId);
				}
				
				p = p.then(() => this.sbUserService.addEditGroupAdmin(depGroup.DeploymentGroupId, this._user)); 				
			}
			else {
				this._user.Embedded.AdministratorOfDeploymentGroups = [];
				p = this.sbUserService.removeGroupAdmin((depGroup || startDepGroup).DeploymentGroupId, this._user.UserId);
			}
		}

		return p;
	}

	/**
	* @ngdoc method
	* @name sbUserDataStore#updateUser
	* @methodOf sharebase.sbUserDataStore
	* @kind function
	*
	* @description
	* Gets an updated user
	*
	* @returns {Deferred} A promise that will resolve with an updated user
	* 
	*/
	public updateUser(user, deploymentId) {
		return this.sbUserService.updateUser(user, deploymentId);
	}

	/**
	* @ngdoc method
	* @name sbUserDataStore#lockUser
	* @methodOf sharebase.sbUserDataStore
	* @kind function
	*
	* @description
	* Locks a user
	*
	* @returns {Deferred} A promise that will resolve
	* 
	*/
	public lockUser(userId, deploymentId) {
		return this.sbUserService.lockUser(userId, deploymentId);
	}

	/**
	* @ngdoc method
	* @name sbUserDataStore#unlockUser
	* @methodOf sharebase.sbUserDataStore
	* @kind function
	*
	* @description
	* Unlocks a user
	*
	* @returns {Deferred} A promise that will resolve
	* 
	*/
	public unlockUser(userId, deploymentId) {
		return this.sbUserService.unlockUser(userId, deploymentId);
	}

	private setTenantAdmin(val:boolean) {
		val = typeof(val) === 'boolean' ? val : false;
		let index = this._user.Embedded.Roles.indexOf('TenantAdmin');
		
		if (val && index < 0) {
			// add role
			this._user.Embedded.Roles.push('TenantAdmin');

		}
		else if (!val && index >= 0) {
			// remove role
			this._user.Embedded.Roles.splice(index, 1);
		}
	}

	private setDeploymentGroupAdmin(val) {
		this._user.Embedded.AdministratorOfDeploymentGroups = typeof(val) === 'undefined' || val === null ? [] : [val];
	}

	private setSiteAdmin(val) {
		let index = this._user.Embedded.Roles.indexOf('SiteAdmin');
		if (!val && index > -1) {
			//remove role
			this._user.Embedded.Roles.splice(index, 1);
			return;
		}

		if (val && index === -1) {
			//add role
			this._user.Embedded.Roles.push('SiteAdmin');
			return;
		}
	}

	private setUserAdmin(val) {
		let index = this._user.Embedded.Roles.indexOf('UserAdmin');
		if (!val && index > -1) {
			//remove role
			this._user.Embedded.Roles.splice(index, 1);
			return;
		}

		if (val && index === -1) {
			//add role
			this._user.Embedded.Roles.push('UserAdmin');
			return;
		}
	}
}



// WEBPACK FOOTER //
// ./src/sharebase/dataStores/userDataStore.ts